import { makeObservable, observable, action, computed } from 'mobx';
import { LayersNodeDto, LayerDto } from '../../types';
import { NodeBaseModel, LayerModel } from '..';
import { SourceModelBase } from '../../../custom_shared/models';

export default class LayersNodeModel extends NodeBaseModel {
    layers: LayerModel[] = [];

    constructor(data: LayersNodeDto, sources: SourceModelBase[]) {
        super(data);

        this.layers = data.layers.map(layer => new LayerModel(this, layer, sources));

        makeObservable(this, {
            layers: observable,
            inputs: computed,
            update: action,
            filterLayers: action,
            replaceLayer: action
        });
    }

    getLayerByInputGuid(guid: string) {
        for (const layer of this.layers) {
            if (layer.inputs.some(input => input.guid === guid)) {
                return layer;
            }
        }

        return null;
    }

    update(data: LayersNodeDto, sources: SourceModelBase[]) {
        data.layers.forEach(layer => {
            const existingLayer = this.layers.find(l => l.id === layer.id);

            if (!existingLayer) {
                this.layers.push(new LayerModel(this, layer, sources));
            } else {
                existingLayer.update(layer, sources);
            }
        });

        this.layers = this.layers.filter(layer => data.layers.some(l => l.id === layer.id));
    }

    filterLayers(layerIds: string[]) {
        this.layers = this.layers.filter(layer => layerIds.includes(layer.id));
    }

    replaceLayer(layerDto: LayerDto, sources: SourceModelBase[]) {
        const existingLayerIndex = this.layers.findIndex(layer => layer.id === layerDto.id);

        if (existingLayerIndex === -1) {
            return;
        }

        const replacementLayer = new LayerModel(this, layerDto, sources);
        this.layers.splice(existingLayerIndex, 1, replacementLayer);
    }

    getInputsAtSameLevel(inputGuid: string) {
        const layer = this.getLayerByInputGuid(inputGuid);
        return layer ? layer.inputs : [];
    }

    get inputs() {
        return this.layers.flatMap(layer => layer.inputs);
    }

    get dto(): LayersNodeDto {
        return {
            id: this.id,
            type: this.type,
            name: this.name,
            layers: this.layers.map(layer => layer.dto),
            childNodes: this.childNodes.map(node => node.dto)
        };
    }
}
